import { DOCUMENT, Injectable, Injector, effect, inject } from '@angular/core';
import { AuthService } from '@privaci/core';

/**
 * The Loqui assistant's embed, as the Loqui portal writes it for the `kiro-app` workspace. The
 * workspace's allowed origins must include every origin this app is served from
 * (https://app.privaci.io, https://dev.app.privaci.io, and http://localhost:4200 for `ng serve`), or
 * the Loqui API answers 403 `embed-not-allowed` and the widget shows "This assistant isn't available
 * on this site". The key is public by design: Loqui checks it against the page's Origin.
 */
export const LOQUI_EMBED = {
  scriptUrl: 'https://cdn.staging.hvitt.dk/loqui.js',
  tenant: 'kiro-app',
  key: 'pk_live_ac7lfpxxx54g7b5vf0ps6ceq',
  endpoint: 'https://api.staging.hvitt.dk',
} as const;

/** Marks the script element this service owns, so teardown never touches anyone else's. */
const OWNER_ATTRIBUTE = 'data-privaci-loqui';

/**
 * Shows the Loqui chat assistant only while a user is signed in.
 *
 * Follows `AuthService.isAuthenticated`: signing in appends the embed script, whose bundle mounts
 * one `<loqui-chat>` on the body from the script's `data-*` attributes; signing out removes both, so
 * the login, register and password screens never carry the widget. The bundle is loaded on demand
 * rather than from index.html for the same reason - an anonymous visitor never downloads it.
 *
 * Signing in again in the same tab appends a fresh script: the bundle mounts from the script that
 * loaded it, so re-running it is the documented way to mount again. Its custom-element definition is
 * guarded, so the second run does not throw, and the browser serves the bundle from its cache.
 */
@Injectable({ providedIn: 'root' })
export class LoquiChatService {
  private readonly auth = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private started = false;

  /** Starts following the session. Idempotent; called once from the root component. */
  start(): void {
    if (this.started) return;
    this.started = true;

    effect(
      () => {
        if (this.auth.isAuthenticated()) this.mount();
        else this.unmount();
      },
      { injector: this.injector },
    );
  }

  private mount(): void {
    if (this.document.querySelector(`script[${OWNER_ATTRIBUTE}]`)) return;

    const script = this.document.createElement('script');
    script.src = LOQUI_EMBED.scriptUrl;
    script.async = true;
    script.setAttribute(OWNER_ATTRIBUTE, '');
    script.setAttribute('data-tenant', LOQUI_EMBED.tenant);
    script.setAttribute('data-key', LOQUI_EMBED.key);
    script.setAttribute('data-endpoint', LOQUI_EMBED.endpoint);
    this.document.body.appendChild(script);
  }

  private unmount(): void {
    // Removing the element runs its disconnectedCallback, which aborts any open chat stream.
    this.document.querySelectorAll('loqui-chat').forEach(element => element.remove());
    this.document.querySelectorAll(`script[${OWNER_ATTRIBUTE}]`).forEach(script => script.remove());
  }
}
